/**
 * mapPrintLegend — render the complete legend onto a canvas side strip for the
 * Print/PDF export (#621 follow-up).
 *
 * Built from the shared legend model (getActiveLegends), NOT screenshotted from
 * the DOM widget — so the printed legend is complete, unscrolled, free of the
 * panel chrome, and identical to what the widget shows.
 */

import { LayerLegend, LegendSymbol } from '../LegendControl/legendModel';
import { faIconToSvg, svgToImage } from '../../layers2/utils/iconUtils';

export interface LegendLayoutOptions {
  width: number;
  padding: number;
  titleHeight: number;
  headerHeight: number;
  entryHeight: number;
  groupGap: number;
}

const DEFAULTS: LegendLayoutOptions = {
  width: 240,
  padding: 12,
  titleHeight: 26,
  headerHeight: 20,
  entryHeight: 20,
  groupGap: 8,
};

export interface LegendLayoutItem {
  kind: 'title' | 'header' | 'entry';
  y: number;
  text?: string;
  label?: string;
  symbol?: LegendSymbol;
}

export interface LegendLayout {
  width: number;
  height: number;
  items: LegendLayoutItem[];
}

/** Pure positioning pass: stack title, per-layer headers, and per-entry rows. */
export function buildLegendLayout(
  legends: LayerLegend[],
  options: Partial<LegendLayoutOptions> = {},
): LegendLayout {
  const opt = { ...DEFAULTS, ...options };
  const items: LegendLayoutItem[] = [];
  let y = opt.padding;

  items.push({ kind: 'title', y, text: 'Map Legend' });
  y += opt.titleHeight;

  for (const legend of legends) {
    items.push({ kind: 'header', y, text: legend.layerName });
    y += opt.headerHeight;
    for (const entry of legend.entries) {
      items.push({ kind: 'entry', y, label: entry.label, symbol: entry.symbol });
      y += opt.entryHeight;
    }
    y += opt.groupGap;
  }

  return { width: opt.width, height: y + opt.padding, items };
}

/** Display height (px) for an icon swatch on the print strip, scaled by symbology size. */
function iconHeightPx(size: number): number {
  return Math.min(18, Math.max(9, Math.round(18 * size)));
}

function symbolKey(symbol: LegendSymbol): string | null {
  if (symbol.kind === 'image') return `img:${symbol.url}`;
  // Print falls back to the first image (e.g. AFF flying icon) for a multi-image row.
  if (symbol.kind === 'images') return symbol.urls[0] ? `img:${symbol.urls[0]}` : null;
  if (symbol.kind === 'icon') return `icon:${symbol.iconName}:${symbol.color}`;
  return null; // swatches are drawn directly, no image to preload
}

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    // Request CORS-clean so a cross-origin legend (e.g. CWFIS/FireSTARR on Azure,
    // which sends Access-Control-Allow-Origin) draws onto the print canvas without
    // tainting it. Harmless for same-origin; a non-CORS host simply fails to load
    // and is skipped, never tainting toDataURL().
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/** Preload every image/icon symbol into HTMLImageElements, keyed for the draw pass. */
async function loadSymbolImages(legends: LayerLegend[]): Promise<Map<string, HTMLImageElement>> {
  const map = new Map<string, HTMLImageElement>();
  const tasks: Promise<void>[] = [];
  const seen = new Set<string>();

  for (const legend of legends) {
    for (const entry of legend.entries) {
      const key = symbolKey(entry.symbol);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const symbol = entry.symbol;
      if (symbol.kind === 'image') {
        tasks.push(loadImage(symbol.url).then((img) => { if (img) map.set(key, img); }));
      } else if (symbol.kind === 'images') {
        tasks.push(loadImage(symbol.urls[0]).then((img) => { if (img) map.set(key, img); }));
      } else if (symbol.kind === 'icon') {
        const svg = faIconToSvg(symbol.iconName, { color: symbol.color, size: 1 });
        if (svg) tasks.push(svgToImage(svg).then((img) => { if (img) map.set(key, img); }).catch(() => {}));
      }
    }
  }

  await Promise.all(tasks);
  return map;
}

function drawSwatch(ctx: CanvasRenderingContext2D, symbol: Extract<LegendSymbol, { kind: 'swatch' }>, x: number, cy: number): void {
  ctx.save();
  ctx.fillStyle = symbol.color;
  ctx.strokeStyle = 'rgba(0,0,0,0.35)';
  ctx.lineWidth = 1;
  if (symbol.shape === 'circle') {
    ctx.beginPath();
    ctx.arc(x + 7, cy, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  } else if (symbol.shape === 'line') {
    ctx.fillRect(x, cy - 1.5, 18, 3);
  } else {
    ctx.fillRect(x, cy - 6, 16, 12);
    ctx.strokeRect(x, cy - 6, 16, 12);
  }
  ctx.restore();
}

/**
 * Render the complete legend to a canvas (white box). Returns null when there
 * is nothing to show. Async because image/icon symbols are loaded first.
 */
export async function renderLegendStrip(
  legends: LayerLegend[],
  options: Partial<LegendLayoutOptions> = {},
): Promise<HTMLCanvasElement | null> {
  if (legends.length === 0) return null;

  const layout = buildLegendLayout(legends, options);
  const images = await loadSymbolImages(legends);

  const canvas = document.createElement('canvas');
  canvas.width = layout.width;
  canvas.height = layout.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // Box background + border.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.strokeStyle = 'rgba(0,0,0,0.25)';
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, canvas.width - 1, canvas.height - 1);

  const pad = options.padding ?? DEFAULTS.padding;
  const symbolX = pad;
  const symbolW = 22;
  const textX = symbolX + symbolW + 8;

  for (const item of layout.items) {
    if (item.kind === 'title') {
      ctx.fillStyle = '#111827';
      ctx.font = 'bold 15px Arial';
      ctx.textBaseline = 'top';
      ctx.fillText(item.text || '', symbolX, item.y);
      continue;
    }
    if (item.kind === 'header') {
      ctx.fillStyle = '#374151';
      ctx.font = 'bold 12px Arial';
      ctx.textBaseline = 'top';
      ctx.fillText(item.text || '', symbolX, item.y + 2);
      continue;
    }

    // entry
    const cy = item.y + 9; // vertical centre of the row's symbol band
    const symbol = item.symbol!;
    if (symbol.kind === 'swatch') {
      drawSwatch(ctx, symbol, symbolX, cy);
    } else {
      const img = images.get(symbolKey(symbol)!);
      if (img) {
        const h = symbol.kind === 'icon' ? iconHeightPx(symbol.size) : 16;
        const ratio = img.width && img.height ? img.width / img.height : 1;
        const w = Math.min(symbolW, Math.round(h * ratio));
        ctx.drawImage(img, symbolX, cy - h / 2, w, h);
      }
    }
    if (item.label) {
      ctx.fillStyle = '#1f2937';
      ctx.font = '11px Arial';
      ctx.textBaseline = 'middle';
      ctx.fillText(item.label, textX, cy);
    }
  }

  return canvas;
}
